import React, { useEffect, useMemo, useRef, useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import "./FilterBar.css";
import { useBodyScrollLock, useIsPhone } from "../viewport";

/* ============================================================
   FilterBar — canonical filter control system for FOX dashboards
   ============================================================

   Every dashboard page currently rolls its own filter buttons,
   location toggles, and date pickers with inline styles that drift
   (border-radius 4/6/8/10/12px, padding "8px 12px" vs "3px 10px", etc).
   These five components are the ONE reusable implementation every
   dashboard should adopt going forward. Colors/radii/spacing come
   from the design tokens in ../index.css — don't hardcode new ones.

   -----------------------------------------------------------
   USAGE EXAMPLES (copy-paste starting points for page migrations)
   -----------------------------------------------------------

   1) Location toggle group (mutually exclusive, segmented control):

     import { FilterBar, FilterButtonGroup, FilterButton } from "./components/FilterBar";

     const LOCATIONS = ["All", "NSA", "BGFC", "PFC"];
     const locVariant = (loc) => (loc === "All" ? "default" : loc.toLowerCase());

     <FilterBar>
       <FilterButtonGroup label="Location">
         {LOCATIONS.map((loc) => (
           <FilterButton
             key={loc}
             label={loc}
             active={selectedLocation === loc}
             variant={locVariant(loc)}
             onClick={() => setSelectedLocation(loc)}
           />
         ))}
       </FilterButtonGroup>
     </FilterBar>

   2) Single active/inactive button pair (e.g. a time-range toggle):

     <FilterButton
       label="This Month"
       active={range === "month"}
       onClick={() => setRange("month")}
     />
     <FilterButton
       label="This Year"
       active={range === "year"}
       onClick={() => setRange("year")}
     />

   3) Dropdown (e.g. activity type):

     import { FilterDropdown } from "./components/FilterBar";

     <FilterDropdown
       label="Activity Type"
       value={selectedActType}
       onChange={(e) => setSelectedActType(e.target.value)}
       options={["All", "Flight", "Sim", "Oral"]}
     />

   4) Date range:

     import { FilterDateRange } from "./components/FilterBar";

     <FilterDateRange
       label="Date Range"
       startValue={startDate}
       endValue={endDate}
       onStartChange={(e) => setStartDate(e.target.value)}
       onEndChange={(e) => setEndDate(e.target.value)}
     />

   ============================================================ */

/* ============================================================
   usePinnedFilterBar — a filter bar that pins under the site header
   ============================================================

   Returns the pieces a page needs to sticky-pin its filter bar and
   carry the page title into it. Two pages use it, and the geometry
   below is the reason it is shared rather than copied: every part of
   it was arrived at by measuring the running app.

     const { sentinelRef, pinned, top } = usePinnedFilterBar();
     ...
     <div ref={sentinelRef} className="fox-pin-sentinel" aria-hidden="true" />
     <FilterBar className={`fox-sticky-filters${pinned ? " is-pinned" : ""}`}
                style={{ top }}>
       {pinned && <div className="fox-pinned-title">{PAGE_TITLE}</div>}
       ...
     </FilterBar>

   The offset is MEASURED, not hardcoded: #fox-header is sticky, padded
   in rem, and reflows to a single taller column under 900px, so any
   constant would overlap at one width and leave a gap at the other.

   The pinned state is measured too, from where the sentinel actually
   sits relative to the header, rather than inferred from a sentinel
   crossing the viewport edge. An IntersectionObserver on a 1px
   sentinel was tried first and never reported in the app, so the bar
   stuck without the class that styles it or shows the title. Reading
   the geometry each frame cannot fail that way.
   ============================================================ */
export const usePinnedFilterBar = () => {
  const sentinelRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(64);
  const [pinned, setPinned] = useState(false);

  useEffect(() => {
    let frame = 0;
    const check = () => {
      frame = 0;
      const sentinel = sentinelRef.current;
      const header = document.getElementById("fox-header");
      // Floored, not rounded. The header's height is fractional -- 0.95rem of
      // padding plus content -- and rounding it down to a whole pixel left a
      // sub-pixel gap between the two, which read as a see-through hairline
      // with the page scrolling behind it. The bar is offset one pixel further
      // up than the header is tall, so it always tucks under rather than
      // meeting it, at any header height or browser zoom. A whole number also
      // keeps this from re-rendering every frame: React bails out when the
      // value is unchanged.
      const top = header ? Math.floor(header.getBoundingClientRect().height) : 0;
      if (top) setHeaderHeight(top);
      if (sentinel) setPinned(sentinel.getBoundingClientRect().top <= top);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };
    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    // The header changes height when its own contents wrap, which a resize
    // event alone misses.
    const observer = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(onScroll) : null;
    const header = document.getElementById("fox-header");
    if (observer && header) observer.observe(header);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return { sentinelRef, pinned, top: Math.max(0, headerHeight - 1) };
};

/**
 * Flex row wrapper for a group of filter controls.
 */
/* Walks the bar's own controls for whatever is currently switched on, so the
   phone's collapsed summary can say what is filtered rather than just that
   something is.

   This introspects components FilterBar itself owns -- FilterButton's `active`,
   FilterDropdown's `value` and `options` -- so it is not reaching into anything it does not
   control. A page that puts something else in the bar simply contributes
   nothing here, and the summary falls back to "Filters". */
function collectActiveLabels(children, found = []) {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child) || found.length >= 4) return;
    const { active, label, value, options } = child.props || {};
    if (active === true && typeof label === "string") {
      found.push(label);
    } else if (typeof value === "string" && value && value !== "All") {
      // What the dropdown shows for its value, where its options carry a
      // label: "Sep 30, 2026", not the "2026-09" behind it.
      const shown = Array.isArray(options)
        ? options.find((opt) => opt && typeof opt === "object" && opt.value === value)
        : null;
      found.push(typeof shown?.label === "string" ? shown.label : value);
    }
    if (child.props?.children) collectActiveLabels(child.props.children, found);
  });
  return found;
}

/* The filter bar. On a desktop this is the same wrapping row it has always
   been -- the element below 768px is the only thing that differs.

   The bar has no media queries of its own and never has: it is a flex row of
   controls that, at phone width, becomes a tall stack the reader must scroll
   past to reach the data it filters. Collapsing it to one button and putting
   the controls in a sheet keeps them one tap away without spending the screen.

   FOX filters apply as they are touched -- each control calls its page's
   setter directly -- so the sheet closes with "Done" rather than staging an
   Apply. Staging would mean every page holding a draft copy of its own filter
   state, which is a change to 20-odd pages rather than to this one. */
export const FilterBar = ({ children, style, className = "" }) => {
  const isPhone = useIsPhone();
  const [sheetOpen, setSheetOpen] = useState(false);

  if (!isPhone) {
    return (
      <div className={`filter-bar ${className}`.trim()} style={style}>
        {children}
      </div>
    );
  }

  const active = collectActiveLabels(children);
  const summary = active.length ? active.slice(0, 2).join(" · ") : "All data";
  const extra = active.length > 2 ? active.length - 2 : 0;

  return (
    <>
      <button
        type="button"
        className="filter-bar-collapsed"
        aria-haspopup="dialog"
        aria-expanded={sheetOpen}
        onClick={() => setSheetOpen(true)}
      >
        <SlidersHorizontal size={17} strokeWidth={2} aria-hidden="true" />
        <span className="filter-bar-collapsed-text">
          <span className="filter-bar-collapsed-title">Filters</span>
          <span className="filter-bar-collapsed-summary">
            {summary}
            {extra > 0 ? ` +${extra}` : ""}
          </span>
        </span>
      </button>
      {sheetOpen && (
        <FilterSheet onClose={() => setSheetOpen(false)}>{children}</FilterSheet>
      )}
    </>
  );
};

function FilterSheet({ children, onClose }) {
  useBodyScrollLock(true);
  const sheetRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    sheetRef.current?.focus();
  }, []);

  return (
    <div className="filter-sheet-root" role="dialog" aria-modal="true" aria-label="Filters">
      <button type="button" className="filter-sheet-scrim" aria-label="Close" tabIndex={-1} onClick={onClose} />
      <div className="filter-sheet" ref={sheetRef} tabIndex={-1}>
        <span className="filter-sheet-grab" aria-hidden="true" />
        <div className="filter-sheet-head">
          <span className="filter-sheet-title">Filters</span>
          <button type="button" className="filter-sheet-close" onClick={onClose} aria-label="Close">
            <X size={17} strokeWidth={2.2} />
          </button>
        </div>
        <div className="filter-sheet-body">{children}</div>
        <div className="filter-sheet-foot">
          <button type="button" className="filter-sheet-done" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

const VARIANT_CLASS = {
  default: "",
  nsa: "filter-btn--nsa",
  bgfc: "filter-btn--bgfc",
  pfc: "filter-btn--pfc",
};

/**
 * A single toggle/pill button — used standalone (e.g. a time-range
 * toggle) or as a child of FilterButtonGroup (e.g. a location option).
 */
export const FilterButton = ({ label, active = false, onClick, variant = "default", disabled = false, title, pressed }) => (
  <button
    type="button"
    className={`filter-btn ${VARIANT_CLASS[variant] || ""} ${active ? "active" : ""}`.trim()}
    onClick={onClick}
    disabled={disabled}
    /* Optional, and omitted entirely when not passed. For the button whose
       two-word label cannot carry its whole meaning -- a comparison basis,
       say -- rather than widening the label until the group wraps. */
    title={title}
    /* Optional too: for a group where several can be on at once, which a
       highlight alone does not tell a screen reader. */
    aria-pressed={pressed}
  >
    {label}
  </button>
);

/**
 * Segmented-control wrapper for a row of mutually-exclusive
 * FilterButtons (e.g. Location: All / NSA / BGFC / PFC).
 */
export const FilterButtonGroup = ({ label, children, style, action }) => (
  <div className="filter-group">
    {(label || action) && (
      <div className="filter-label-row">
        {label && <span className="filter-label">{label}</span>}
        {action}
      </div>
    )}
    <div className="filter-btn-group" style={style}>
      {children}
    </div>
  </div>
);

/**
 * Labeled select dropdown. `options` accepts either an array of
 * strings or an array of { value, label } objects. Use multiSelect
 * for checklist dropdown filters such as multi-year selectors.
 */
export const FilterDropdown = ({
  label,
  value,
  onChange,
  options = [],
  disabled = false,
  multiSelect = false,
  onOptionToggle,
  onSelectAll,
  onClearAll,
  allSelectedLabel = "All",
  emptyLabel = "None",
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const normalizedOptions = useMemo(
    () => options.map((opt) => ({
      value: typeof opt === "object" ? opt.value : opt,
      label: typeof opt === "object" ? opt.label : opt,
    })),
    [options]
  );
  const isLongList = normalizedOptions.length > 12;

  useEffect(() => {
    if (!open) return undefined;

    const handleMouseDown = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open]);

  if (!multiSelect) {
    const selectedOption = normalizedOptions.find((opt) => opt.value === value);
    const buttonLabel = selectedOption?.label || emptyLabel;

    return (
      <div className="filter-group">
        {label && <span className="filter-label">{label}</span>}
        <div
          className={`filter-dropdown-wrap ${isLongList ? "filter-dropdown-wrap--long" : ""}`.trim()}
          ref={dropdownRef}
        >
          <button
            type="button"
            className="filter-dropdown filter-dropdown-trigger"
            onClick={() => setOpen((prev) => !prev)}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            // The visible text is the current value, so without this the
            // control announces "KMKT - NSA" with no hint of what it selects.
            aria-label={label ? `${label}: ${buttonLabel}` : undefined}
          >
            <span className="filter-dropdown-label">{buttonLabel}</span>
            <span className="filter-dropdown-caret">v</span>
          </button>

          {open && (
            <div className="filter-dropdown-panel" role="listbox">
              <div className="filter-dropdown-options">
                {normalizedOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`filter-dropdown-option filter-dropdown-option-button ${value === opt.value ? "selected" : ""}`.trim()}
                    onClick={() => {
                      onChange?.({ target: { value: opt.value } });
                      setOpen(false);
                    }}
                    role="option"
                    aria-selected={value === opt.value}
                  >
                    <span>{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const selectedValues = Array.isArray(value) ? value : [];
  const selectedOptions = normalizedOptions.filter((opt) => selectedValues.includes(opt.value));
  const buttonLabel = (() => {
    if (normalizedOptions.length > 0 && selectedOptions.length === normalizedOptions.length) return allSelectedLabel;
    if (selectedOptions.length === 0) return emptyLabel;
    const visibleLabels = selectedOptions.slice(0, 2).map((opt) => opt.label).join(", ");
    const hiddenCount = selectedOptions.length - 2;
    return hiddenCount > 0 ? `${visibleLabels} +${hiddenCount} more` : visibleLabels;
  })();

  const handleOptionChange = (optValue) => {
    if (onOptionToggle) {
      onOptionToggle(optValue);
      return;
    }

    const nextValue = selectedValues.includes(optValue)
      ? selectedValues.filter((item) => item !== optValue)
      : [...selectedValues, optValue];
    onChange?.(nextValue);
  };

  return (
    <div className="filter-group">
      {label && <span className="filter-label">{label}</span>}
      <div
        className={`filter-dropdown-wrap ${isLongList ? "filter-dropdown-wrap--long" : ""}`.trim()}
        ref={dropdownRef}
      >
        <button
          type="button"
          className="filter-dropdown filter-dropdown-trigger"
          onClick={() => setOpen((prev) => !prev)}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="filter-dropdown-label">{buttonLabel}</span>
          <span className="filter-dropdown-caret">v</span>
        </button>

        {open && (
          <div className="filter-dropdown-panel" role="listbox" aria-multiselectable="true">
            <div className="filter-dropdown-actions">
              <button type="button" className="filter-dropdown-action" onClick={onSelectAll}>
                Select All
              </button>
              <button type="button" className="filter-dropdown-action" onClick={onClearAll}>
                Clear All
              </button>
            </div>

            <div className="filter-dropdown-options">
              {normalizedOptions.map((opt) => (
                <label key={opt.value} className="filter-dropdown-option">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(opt.value)}
                    onChange={() => handleOptionChange(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Checkbox paired with a numeric input for threshold-style filters.
 */
export const FilterCheckboxInput = ({
  label,
  checked = false,
  onCheckedChange,
  value,
  onValueChange,
  unit,
  disabled = false,
}) => {
  const valueDisabled = disabled || !checked;

  return (
    <div className="filter-group filter-checkbox-input-group">
      <span className="filter-label filter-label-placeholder" aria-hidden="true">
        &nbsp;
      </span>
      <div className="filter-checkbox-input">
        <label className={`filter-checkbox-label ${disabled ? "disabled" : ""}`.trim()}>
          <input
            type="checkbox"
            className="filter-checkbox"
            checked={checked}
            onChange={(event) => onCheckedChange?.(event.target.checked)}
            disabled={disabled}
          />
          <span>{label}</span>
        </label>
        <input
          type="number"
          className="filter-number-input"
          value={value}
          onChange={onValueChange}
          disabled={valueDisabled}
        />
        {unit && <span className={`filter-input-unit ${valueDisabled ? "disabled" : ""}`.trim()}>{unit}</span>}
      </div>
    </div>
  );
};

/**
 * A date range input pair with consistent styling.
 */
export const FilterDateRange = ({
  label,
  startLabel = "",
  endLabel = "",
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  action = null,
  disabled = false,
}) => (
  <div className="filter-group">
    {label && <span className="filter-label">{label}</span>}
    <div className="filter-date-range-inputs">
      <label className="filter-date-field">
        {startLabel && <span className="filter-date-field-label">{startLabel}</span>}
        <input
          type="date"
          className="filter-date-input"
          value={startValue}
          onChange={onStartChange}
          disabled={disabled}
        />
      </label>
      <span className="filter-date-sep">–</span>
      <label className="filter-date-field">
        {endLabel && <span className="filter-date-field-label">{endLabel}</span>}
        <input
          type="date"
          className="filter-date-input"
          value={endValue}
          onChange={onEndChange}
          disabled={disabled}
        />
      </label>
      {action && <div className="filter-date-action">{action}</div>}
    </div>
  </div>
);
